import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Button, Separator} from 'platform/components';
import {Hide, HStack, Show, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {lazy, Suspense} from 'react';

import {equals, isNil, isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {formatDocumentTitle} from '@dms/shared';

import {useDebouncedValue} from 'shared/web';

import {AuctionPreview} from '../../../components/AuctionPreview/AuctionPreview';
import {SourcingDataStatus} from '../../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../../components/SourcingTable/SourcingTable';
import {VehiclePreview} from '../../../components/VehiclePreview/VehiclePreview';
import {AUCTION_DEFAULT_ORDER_ID} from '../../../constants/AUCTION_DEFAULT_ORDER_ID';
import {AUCTION_ORDER_BY_MAP, AUCTION_SORT_OPTIONS} from '../../../constants/auctionSortOptions';
import {CLASSIFIEDS_DEFAULT_ORDER_ID} from '../../../constants/CLASSIFIEDS_DEFAULT_ORDER_ID';
import {
  CLASSIFIEDS_ALL_OFFER_ORDER_BY_MAP,
  CLASSIFIEDS_ALL_OFFER_SORT_OPTIONS,
} from '../../../constants/classifiedsSortBy';
import {useAuctionEnumLookup} from '../../../hooks/useAuctionEnumLookup';
import {useSortOrderByFromUrl} from '../../../hooks/useSortOrderByFromUrl';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {getAuctionTableColumns} from '../../../utils/getAuctionTableColumns';
import {getSourcingVehicleId} from '../../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../../utils/isAuctionVehicle';
import {isUnifiedSourcingEnabled} from '../../../utils/isUnifiedSourcingEnabled';
import {useAllOffersListing} from '../hooks/useAllOffersListing';
import {useAllOffersMapPreview} from '../hooks/useAllOffersMapPreview';
import {useAllOffersMapState} from '../hooks/useAllOffersMapState';
import {useFilterCount} from '../hooks/useFilterCount';
import {useFilterDraft} from '../hooks/useFilterDraft';
import {useFilterEditingSession} from '../hooks/useFilterEditingSession';
import {useSourcingFilterBodies} from '../hooks/useSourcingFilterBodies';
import {hasInvalidFilterRange} from '../utils/hasInvalidFilterRange';
import {ClassifiedsMapSkeleton} from './ClassifiedsMap/components/ClassifiedsMapSkeleton';
import {MapPreviewPlaceholder} from './ClassifiedsMap/components/MapPreviewPlaceholder';
import {FilterEditor} from './FilterEditor/FilterEditor';
import {SavedFiltersMobileBar} from './FilterPresetButtons/components/SavedFiltersMobileBar';
import {FilterPresetButtons} from './FilterPresetButtons/FilterPresetButtons';
import {Filters} from './Filters/Filters';
import {FiltersContextProvider, useFilters} from './FiltersContext/FiltersContext';
import {FilterTags} from './FilterTags/FilterTags';
import {LegacyAllOffersPage} from './LegacyAllOffersPage';

const ClassifiedsMap = lazy(() =>
  import('./ClassifiedsMap/ClassifiedsMap').then((module) => ({
    default: module.ClassifiedsMap,
  }))
);

export function AllOffersPage() {
  if (!isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN)) {
    return <LegacyAllOffersPage />;
  }
  return <EditableAllOffersPage />;
}

function EditableAllOffersPage() {
  const applied = useFilters();
  const draft = useFilterDraft();
  return (
    <FiltersContextProvider value={draft.context}>
      <AllOffersContent draft={draft} applied={applied} />
    </FiltersContextProvider>
  );
}

function AllOffersContent(props: {
  draft: ReturnType<typeof useFilterDraft>;
  applied: ReturnType<typeof useFilters>;
}) {
  const sourcingType = useSourcingType();
  const enumLookup = useAuctionEnumLookup();
  const formatNumber = useFormatNumber();
  const {
    isFiltersOpen,
    editingField,
    isEditing,
    filterPresetsControl,
    listingSnapshot,
    openFiltersView,
    applyEditing,
    continueEditing,
    editChip,
    cancelChip,
  } = useFilterEditingSession({
    draft: props.draft,
    getListingSnapshot: () => ({
      classifieds: classifiedsFilterBody,
      auctions: auctionsFilterBody,
      count,
    }),
  });
  const [classifiedsState, classifiedsDispatch] = useSourcingTable(
    'all-vehicles',
    CLASSIFIEDS_DEFAULT_ORDER_ID,
    filterPresetsControl.appliedFilterPresetId
  );
  const [auctionsState, auctionsDispatch] = useSourcingTable(
    'auctions',
    AUCTION_DEFAULT_ORDER_ID,
    filterPresetsControl.appliedFilterPresetId
  );
  const [state, dispatch] =
    sourcingType === SourcingType.CLASSIFIEDS
      ? [classifiedsState, classifiedsDispatch]
      : [auctionsState, auctionsDispatch];
  const selectedAdId = state.selectedRow?.sourcingVehicle?.adId;
  const {
    isMapView,
    isClassifiedsMapEnabled,
    visibleViewMode,
    savedMapPreviewAdId,
    onChangeViewMode,
    onSelectMapAd,
    onOpenDetail,
  } = useAllOffersMapState({
    source: sourcingType,
    selectedAdId,
    tableName: state.tableName,
    clearListSelection: () => classifiedsDispatch({type: 'selectRow', row: null}),
  });
  // The stored toggle outlives the flag, so a hidden switch must not keep polling.
  const isLivePollingEnabled =
    state.isRealTime && isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT);
  const sourcingCurrency = useSourcingCurrency();
  const sourcingTargetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const {filters} = useFilters();
  const isInvalidRange = hasInvalidFilterRange(filters);
  const debouncedFilters = useDebouncedValue(filters, 500);
  const filterBody = isEditing ? debouncedFilters : filters;

  const classifiedsColumns = useSourcingTableColumns();
  const auctionColumns = getAuctionTableColumns({formatNumber, enumLookup});
  const columns = sourcingType === SourcingType.CLASSIFIEDS ? classifiedsColumns : auctionColumns;

  const sortByOptions =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_SORT_OPTIONS
      : CLASSIFIEDS_ALL_OFFER_SORT_OPTIONS;
  const {orderBy: sortOrderByFromUrl, clear: clearUrlSort} = useSortOrderByFromUrl();
  const orderBy =
    sortOrderByFromUrl ??
    (sourcingType === SourcingType.AUCTIONS
      ? AUCTION_ORDER_BY_MAP.get(state.orderBy)
      : CLASSIFIEDS_ALL_OFFER_ORDER_BY_MAP.get(state.orderBy));
  const currency = sourcingCurrency?.code;
  const country = sourcingTargetCountry?.code;

  const filterBodies = useSourcingFilterBodies(filterBody, filterPresetsControl, country);
  const classifiedsFilterBody = filterBodies.classifieds;
  const auctionsFilterBody = filterBodies.auctions;
  const listingBodies = isEditing ? listingSnapshot : filterBodies;

  const countQuery = useFilterCount({
    classifieds: isNotNil(classifiedsFilterBody)
      ? {body: classifiedsFilterBody, currency, pricingStrategy}
      : skipToken,
    auctions: isNotNil(auctionsFilterBody)
      ? {body: auctionsFilterBody, currency, pricingStrategy}
      : skipToken,
    source: sourcingType,
    isPending: (isEditing && !equals(filters, debouncedFilters)) || filterPresetsControl.isLoading,
    isInvalid: isInvalidRange,
    isRealTime: isLivePollingEnabled,
  });
  const {count, isLoading: isCountLoading, isUnavailable: isCountUnavailable} = countQuery;
  const chipEditor = (
    <Show when={editingField}>
      {(field) => (
        <FilterEditor
          field={field}
          onApply={applyEditing}
          onContinue={isFiltersOpen ? continueEditing : undefined}
          onCancel={cancelChip}
          count={count}
          isLoading={isCountLoading}
          isDisabled={isCountUnavailable}
          hasError={countQuery.isError}
          onRetry={countQuery.refetch}
        />
      )}
    </Show>
  );
  const mapPreviewAdId = useAllOffersMapPreview({
    isMapView,
    isEditing,
    classifiedsFilterBody,
    savedMapPreviewAdId,
    country,
    currency,
    pricingStrategy,
  });

  const {
    tableQueryResult,
    hasCompletedWithNoVisibleResults,
    preview,
    listQueryKey,
    isFetchingFreshData,
    hasFilters,
  } = useAllOffersListing({
    listingBodies,
    sourcingType,
    isEditing,
    isMapView,
    isLivePollingEnabled,
    state,
    country,
    currency,
    pricingStrategy,
    orderBy,
    enumLookup,
    filterPresetsControl,
    appliedFilters: props.applied.filters,
    count,
    listingSnapshot,
    selectedAdId,
  });

  const pageTitle = formatDocumentTitle(
    sourcingType === SourcingType.CLASSIFIEDS
      ? i18n.t('page.sourcing.labels.classifieds')
      : i18n.t('page.sourcing.labels.auctions'),
    i18n.t('page.sourcing.labels.sourcing')
  );

  if (isFiltersOpen) {
    return (
      <>
        <Helmet title={pageTitle} />
        <Filters
          filterPresetsControl={filterPresetsControl}
          count={count}
          isLoading={isCountLoading}
          isDisabled={isCountUnavailable}
          hasError={countQuery.isError}
          onRetry={countQuery.refetch}
          onClose={applyEditing}
          onEdit={editChip}
          data-testid={testIds.sourcing.classifieds('filters')}
        />
        {chipEditor}
      </>
    );
  }

  const filtersButton = (
    <Button
      variant={hasFilters ? 'primary' : 'outlined'}
      title={i18n.t('page.filters.labels.filters')}
      leftIcon="content/filter_list"
      onClick={openFiltersView}
      data-testid={testIds.sourcing.classifieds('filters')}
    />
  );

  return (
    <>
      {chipEditor}
      <SourcingLayout
        hasPreview={isMapView ? isNotNil(mapPreviewAdId) : isNotNil(preview) || isFetchingFreshData}
        isPreviewLoading={!isMapView && isFetchingFreshData && isNil(preview)}
        pageTitle={pageTitle}
        header={
          <VStack width="100%" spacing={4}>
            <Hide onMobile onTablet>
              <HStack spacing={4}>
                {filtersButton}
                <Show when={!filterPresetsControl.isEmpty}>
                  <Separator orientation="vertical" spacing={0} />
                </Show>
                <FilterPresetButtons filterPresetsControl={filterPresetsControl} isCollapsible />
              </HStack>
            </Hide>

            <Show onMobile onTablet>
              <HStack spacing={2} align="center" wrap justify="space-between">
                {filtersButton}
                <SavedFiltersMobileBar filterPresetsControl={filterPresetsControl} />
              </HStack>
            </Show>

            <Show when={hasFilters}>
              <FiltersContextProvider value={props.applied}>
                <FilterTags
                  filterPresetsControl={filterPresetsControl}
                  onEdit={editChip}
                  data-testid={testIds.sourcing.classifieds('filterHeaderChips')}
                />
              </FiltersContextProvider>
            </Show>
          </VStack>
        }
        content={
          <SourcingDataStatus
            isEmpty={hasCompletedWithNoVisibleResults}
            iconUrl="/assets/images/empty_images/empty-sourcing.svg"
            text={i18n.t('general.notifications.noResults')}
            textSecondLine={i18n.t('page.filters.notifications.removeSearchFilters')}
            noResultsContent={
              <Button
                title={i18n.t('page.filters.labels.filters')}
                leftIcon="content/filter_list"
                onClick={openFiltersView}
                data-testid="button-classifieds-allOffers-empty.filter"
              />
            }
          >
            <SourcingTable
              state={state}
              dispatch={dispatch}
              columns={columns}
              queryResult={tableQueryResult}
              listQueryKey={listQueryKey}
              sortByOptions={sortByOptions}
              isRealTimeEnabled
              realTimeOrderBy={
                sourcingType === SourcingType.CLASSIFIEDS ? CLASSIFIEDS_DEFAULT_ORDER_ID : undefined
              }
              isSortByUnset={isNotNil(sortOrderByFromUrl)}
              onClearUrlSort={clearUrlSort}
              viewMode={visibleViewMode}
              onViewModeChange={onChangeViewMode}
              mapView={
                sourcingType === SourcingType.CLASSIFIEDS && isClassifiedsMapEnabled ? (
                  <Suspense
                    fallback={
                      isUnifiedSourcingEnabled() ? (
                        <ClassifiedsMapSkeleton />
                      ) : (
                        i18n.t('page.sourcing.map.loading')
                      )
                    }
                  >
                    <ClassifiedsMap
                      filterBody={listingBodies?.classifieds ?? null}
                      selectedAdId={mapPreviewAdId}
                      onSelectAd={(adId) => onSelectMapAd(adId, mapPreviewAdId)}
                      onOpenAdDetail={onOpenDetail}
                      onOpenFilters={openFiltersView}
                      isRealTime={isLivePollingEnabled}
                    />
                  </Suspense>
                ) : undefined
              }
            />
          </SourcingDataStatus>
        }
        iframe={(() => {
          if (hasCompletedWithNoVisibleResults) {
            return null;
          }

          if (isMapView) {
            return isNotNil(mapPreviewAdId) ? (
              <VehiclePreview key={mapPreviewAdId} vehicleId={mapPreviewAdId} />
            ) : (
              <MapPreviewPlaceholder />
            );
          }

          return sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(preview) ? (
            <AuctionPreview
              key={preview.auctionVehicle.adId}
              auction={preview}
              data-testid={testIds.sourcing.auctions('preview')}
            />
          ) : (
            <VehiclePreview
              key={getSourcingVehicleId(preview)}
              vehicleId={getSourcingVehicleId(preview)}
            />
          );
        })()}
      />
    </>
  );
}
